import { formatParisDateTime } from '@flux/shared';
import { AlertTriangle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import type { DashboardMaintenance } from '@flux/shared';
import { useDisableMaintenance } from './use-dashboard-actions.js';

export interface MaintenanceCardProps {
  maintenance: DashboardMaintenance;
}

/**
 * Bandeau « Envois vers Martelet suspendus » du tableau de bord (`02` §2),
 * distinct du bandeau global de `components/layout/maintenance-banner.tsx`.
 * « Reprendre les envois » passe par `ConfirmDialog` (`02` §0 : action
 * sensible, conséquence rappelée).
 */
export function MaintenanceCard({ maintenance }: MaintenanceCardProps): JSX.Element | null {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const disable = useDisableMaintenance();

  if (!maintenance.enabled) {
    return null;
  }

  const since = maintenance.since ? formatParisDateTime(new Date(maintenance.since)) : null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-status-red-border bg-status-red-bg px-4 py-3">
      <p className="flex min-w-0 flex-1 items-start gap-2 text-sm text-status-red-fg">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className="font-semibold">Envois vers Martelet suspendus</span>
          {since ? (
            <>
              {' depuis le '}
              <span className="tabular-nums">{since}</span>
            </>
          ) : null}
          {maintenance.reason ? ` (${maintenance.reason})` : ''}.
        </span>
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setConfirmOpen(true);
        }}
      >
        Reprendre les envois
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Reprendre les envois vers Martelet"
        description="Les nouvelles commandes seront de nouveau envoyées immédiatement à Martelet. Les fichiers déjà en attente y restent jusqu'à un envoi manuel (Paramètres › Martelet)."
        confirmLabel="Reprendre"
        isConfirming={disable.isPending}
        onConfirm={() => {
          disable.mutate(undefined, { onSuccess: () => setConfirmOpen(false) });
        }}
      />
    </div>
  );
}
